import { useState, useEffect } from 'react';
import logo from '@/assets/images/logo.png';
import { Button, Label, Loader } from '@/components';
import { useAuth, useTheme } from '@/context';

const REALMS = [{ value: 'st_eng1', label: 'st_eng1' }];

export const Login = ({ onLoginStart }) => {
  const { login, error: authError } = useAuth();
  const { theme, ThemeMode } = useTheme();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [tenantId, setTenantId] = useState('st_eng1');
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    onLoginStart?.();

    try {
      await login({ username, password, tenantId });
    } catch (err) {
      console.error('Login submit error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const isDark = theme === ThemeMode.DARK;

  // Prevent hydration mismatch or flash by rendering nothing until mounted
  if (!mounted) return null;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 transition-all duration-500 ease-in-out"
      style={{
        background: isDark
          ? 'radial-gradient(circle at center, #1e293b 0%, #0f172a 40%, #000000 100%)'
          : 'radial-gradient(circle at center, #e2e8f0 0%, #f8fafc 40%, #ffffff 100%)',
      }}
    >
      {/* Subtle grid pattern overlay for texture */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      ></div>

      {/* Overlay for better text contrast if needed */}
      <div
        className={`absolute inset-0 ${isDark ? 'bg-black/20' : 'bg-white/10'} backdrop-blur-[0px] transition-colors duration-500`}
      ></div>

      <div className="w-full max-w-[420px] relative z-10 flex flex-col gap-6 animate-in fade-in zoom-in-95 duration-500">
        {/* Logo Section */}
        <div className="flex justify-center mb-2">
          <img
            src={logo}
            alt="Logo"
            className={`h-16 w-auto object-contain transition-all duration-500 ${isDark ? 'brightness-0 invert' : ''}`}
          />
        </div>

        {/* Card */}
        <div
          className={`
          backdrop-blur-xl rounded-2xl shadow-2xl border p-8 transition-all duration-500
          ${
            isDark
              ? 'bg-gray-900/40 border-white/10 shadow-black/50'
              : 'bg-white/60 border-white/40 shadow-xl shadow-blue-900/5'
          }
        `}
        >
          <div className="mb-8 text-center space-y-2">
            <h1 className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Field Ops
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label
                htmlFor="username"
                className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
              >
                Username
              </Label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                autoComplete="username"
                className={`
                  w-full px-4 py-3 rounded-lg border text-sm transition-all duration-200 outline-none
                  ${
                    isDark
                      ? 'bg-black/20 border-white/10 text-white placeholder-gray-600 focus:border-blue-500/50 focus:bg-black/40 focus:ring-4 focus:ring-blue-500/10'
                      : 'bg-white/50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10'
                  }
                `}
                placeholder="Enter your username"
              />
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="password"
                className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
              >
                Password
              </Label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className={`
                  w-full px-4 py-3 rounded-lg border text-sm transition-all duration-200 outline-none
                  ${
                    isDark
                      ? 'bg-black/20 border-white/10 text-white placeholder-gray-600 focus:border-blue-500/50 focus:bg-black/40 focus:ring-4 focus:ring-blue-500/10'
                      : 'bg-white/50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10'
                  }
                `}
                placeholder="Enter your password"
              />
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="realm"
                className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
              >
                Organization
              </Label>
              <div className="relative">
                <select
                  id="tenantId"
                  value={tenantId}
                  onChange={e => setTenantId(e.target.value)}
                  className={`
                    w-full px-4 py-3 rounded-lg border text-sm transition-all duration-200 outline-none appearance-none
                    ${
                      isDark
                        ? 'bg-black/20 border-white/10 text-white focus:border-blue-500/50 focus:bg-black/40 focus:ring-4 focus:ring-blue-500/10'
                        : 'bg-white/50 border-gray-200 text-gray-900 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10'
                    }
                  `}
                >
                  {REALMS.map(dept => (
                    <option
                      key={dept.value}
                      value={dept.value}
                      className={isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}
                    >
                      {dept.label}
                    </option>
                  ))}
                </select>
                <div
                  className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </div>

            {authError && (
              <div
                className={`
                px-4 py-3 rounded-lg text-sm border
                ${isDark ? 'bg-red-500/10 border-red-500/20 text-red-200' : 'bg-red-50 border-red-200 text-red-600'}
              `}
              >
                {authError}
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className={`
                w-full py-6 mt-4 font-semibold text-base transition-all duration-300 transform active:translate-y-[0px]
                ${
                  isDark
                    ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/20'
                    : 'bg-gray-900 hover:bg-gray-800 text-white shadow-lg shadow-gray-900/20'
                }
              `}
              size="lg"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader size="sm" className="text-white/80" />
                  <span>Signing in...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};
